import React, { useEffect, useRef, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { CouponFilters } from "../../../../../models/inventory/CouponsFilters";
import AppSearchDropDown from "../../../../../views/components/common/filter/AppSearchDropDown";
import { SortType } from "../../../../../models/SortType";
import CouponsTable from "../../components/Inventory/CouponsTable";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { Coupon } from "../../../../../models/inventory/Coupon";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CouponDetailsPopup from "../../components/Inventory/CouponDetailsPopup";
import CouponActionPopup from "./CouponActionPopup";
import ActivityLogDrawer from "../../../../../views/components/common/drawer/ActivityLogDrawer";
import { CouponActionConfirm } from "../../../stores/CouponStore";

const MAX_RECIPIENT_SEARCH_LENGTH = 100;

interface CouponsSelectedFilters {
  search_text: string;
  coupon_id: FilterValue[];
  order_id: FilterValue[];
  customer_id: FilterValue[];
  inventory_id: FilterValue[];
  status: string;
  active: boolean;
  is_onelink: string | null;
}

const ACTION_OPTIONS: FilterValue[] = [
  { id: "send_reminders", value: "Send Reminders" },
  { id: "download_shipments", value: "Download Shipments" },
  { id: "download_report", value: "Download Report" },
  { id: "expire", value: "Expire" },
  { id: "expire_all", value: "Expire all" },
  { id: "delete", value: "Delete" },
  { id: "delete_all", value: "Delete All" },
  { id: "change_expiry", value: "Change expiry date" },
  { id: "reactivate", value: "Reactivate" },
  { id: "reactivate_all", value: "Reactivate all" },
];

const getSingleId = (value?: number | number[]) => {
  if (Array.isArray(value)) {
    return value.length === 1 && Number(value[0]) > 0
      ? Number(value[0])
      : undefined;
  }
  return value && Number(value) > 0 ? Number(value) : undefined;
};

const COUPON_STATUS_REDEEMED = "5";

const getStatusFromSearchParams = (searchParams: URLSearchParams) => {
  const status = searchParams.get("status");
  if (status) return status;
  if (searchParams.get("redeemed") === "true") return COUPON_STATUS_REDEEMED;
  return "0";
};

const isRedeemedStatus = (status?: string | number) =>
  String(status) === COUPON_STATUS_REDEEMED;

const COUPON_STATUS_EXPIRED = "6";
const DELETE_ALL_STATUSES = new Set(["0", "1", "4"]);

const isExpiredListStatus = (status?: string | number) =>
  String(status) === COUPON_STATUS_EXPIRED;

/**
 * Says whether Expire all belongs on this status.
 * Redeemed and Expired lists cannot expire the order.
 *
 * @param status - Applied status filter. `5` is Redeemed and `6` is Expired.
 * @returns True for every other status, including Select.
 */
const canShowExpireAll = (status?: string | number) =>
  !isRedeemedStatus(status) && !isExpiredListStatus(status);

/**
 * Says whether Delete all belongs on this status.
 * Only Select, Not Sent, and Pending Redemption are lists of coupons that can still be deleted.
 *
 * @param status - Applied status filter. `0` Select, `1` Not Sent, `4` Pending Redemption.
 * @returns True when Delete all may be shown.
 */
const canShowDeleteAll = (status?: string | number) =>
  DELETE_ALL_STATUSES.has(String(status ?? "0"));

const FALLBACK_ERROR_MESSAGE = "Something went wrong! Please try again later.";

const SHIPMENTS_DOWNLOAD_EMPTY_MESSAGE =
  "Download Shipments only includes redeemed coupons for the selected order that do not already have a shipment. None were found. Choose an order with redeemed coupons that still need a shipment, or use Download Report to export all coupons for a customer.";

const isShipmentsDownloadEmptyError = (message: string) =>
  /shipment is not created|do not already have a shipment|still need a shipment/i.test(
    message,
  );

const isCouponActive = (coupon: Coupon) => Boolean(coupon.active);

const hasCouponExpiryPassed = (coupon: Coupon) =>
  coupon.expiry_date ? new Date() > new Date(coupon.expiry_date) : false;

type CouponWriteKind = "onelink" | "redeemed" | "inactive" | "expired" | "pending";

/**
 * Classifies a coupon for the Actions menu from the flags the write APIs use.
 *
 * @param coupon - Row on the current page.
 * @returns One Link, redeemed, inactive, expired, or pending. One Link and redeemed win over the other flags.
 */
const couponWriteKind = (coupon: Coupon): CouponWriteKind => {
  if (coupon.is_onelink) return "onelink";
  if (coupon.redeemed) return "redeemed";
  if (!isCouponActive(coupon)) return "inactive";
  if (coupon.expired) return "expired";
  return "pending";
};

/**
 * Chooses which write actions belong in the menu for the current selection.
 * Nothing selected on an allowed status shows Expire all, and Delete all when one order is applied.
 * A pending selection shows Expire and Delete, not the all-actions. Redeemed shows no write action.
 *
 * @param selected - Coupons the admin has checked. Empty when nothing is checked.
 * @param onActiveList - True when the applied Active filter is Active.
 * @param status - Applied status filter.
 * @param oneOrder - True when the filter or the selection is a single order.
 * @returns Action ids to render. Empty when no write action fits.
 */
const couponWriteActions = (
  selected: Coupon[],
  onActiveList: boolean,
  status?: string | number,
  oneOrder = false,
): string[] => {
  if (isRedeemedStatus(status)) return [];

  if (selected.length === 0) {
    const actions: string[] = [];
    if (!onActiveList) {
      if (oneOrder) actions.push("reactivate_all");
      return actions;
    }
    if (canShowExpireAll(status)) actions.push("expire_all");
    if (oneOrder && canShowDeleteAll(status)) actions.push("delete_all");
    if (oneOrder && isExpiredListStatus(status)) actions.push("reactivate_all");
    return actions;
  }
  const kinds = new Set(selected.map(couponWriteKind));
  if (kinds.size !== 1) return [];

  const only = Array.from(kinds)[0];
  if (only === "pending") {
    const actions = ["expire"];
    if (!isExpiredListStatus(status)) actions.push("delete");
    actions.push("change_expiry");
    if (selected.some((coupon) => hasCouponExpiryPassed(coupon))) {
      actions.push("reactivate");
    }
    return actions;
  }
  if (only === "expired") return ["change_expiry", "reactivate"];
  if (only === "inactive") return ["reactivate"];
  return [];
};

/**
 * Grays a write action that is already in the menu when the admin still has to pick one order or a coupon.
 *
 * @param actionId - Actions menu id.
 * @param selected - Coupons the admin has checked.
 * @param oneOrder - True when the filter or the selection is a single order.
 * @returns True when the row should be gray.
 */
const isCouponWriteGray = (
  actionId: string,
  selected: Coupon[],
  oneOrder: boolean,
) => {
  if (actionId === "expire_all") return !oneOrder;
  if (actionId === "delete_all") return !oneOrder;
  if (actionId === "reactivate_all") return !oneOrder;
  return selected.length === 0 || !oneOrder;
};

const getCouponIdsLabel = (coupons: Coupon[]) =>
  coupons.map((coupon) => coupon.id).join(", ");

const getCaughtErrorMessage = (err: unknown) => {
  if (
    err instanceof Error &&
    err.message &&
    !err.message.startsWith("Request failed with status code")
  ) {
    return err.message;
  }

  const data = (err as { response?: { data?: { message?: string } } })?.response
    ?.data;
  if (data && typeof data.message === "string" && data.message) {
    return data.message;
  }

  return FALLBACK_ERROR_MESSAGE;
};

/**
 * Turns a finished coupon job into the short success line other admin jobs use.
 * Coupon ids and skip reasons stay off this screen.
 *
 * @param verb - Deleting, Expiring, or Reactivating, from the confirm that started the job.
 * @param summary - `job_records` summary. The first number is how many coupons were saved.
 * @param skippedCount - Coupons left unchanged. Zero omits that clause.
 * @returns A single sentence, for example "Reactivated 998 coupons. 2 were left unchanged."
 */
const shortCouponJobMessage = (
  verb: string,
  summary: string | null | undefined,
  skippedCount: number,
) => {
  const saved = String(summary || "").match(/(\d+)/)?.[1];
  const action =
    verb === "Deleting" ? "Deleted" : verb === "Expiring" ? "Expired" : "Reactivated";
  const count = saved ? `${saved} coupons` : "the coupons";
  if (skippedCount > 0) {
    return `${action} ${count}. ${skippedCount} were left unchanged.`;
  }
  return `${action} ${count}.`;
};

const CouponsListing: React.FC = () => {
  const theme = useTheme();
  const { couponStore, customerStore, inventoryStore, orderStore, userStore } =
    useStores();

  const { showError, showWarning, showSuccess } = useNotification();

  const [selectedShipmentIdsMap, setSelectedShipmentIdsMap] = useState<
    Map<number, number>
  >(new Map<number, number>());
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialFilters: CouponsSelectedFilters = {
    search_text: (searchParams.get("search_text") || "").trim(),
    coupon_id: searchParams.get("coupon_id")
      ? searchParams
          .get("coupon_id")!
          .split(",")
          .map((id) => ({ id: id, value: id }))
      : [],
    order_id: searchParams.get("order_id")
      ? searchParams
          .get("order_id")!
          .split(",")
          .map((id) => ({ id: id, value: id }))
      : [],
    customer_id: searchParams.get("customer_id")
      ? searchParams
          .get("customer_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":")!;
            return { id: list[0], value: list[1] };
          })
      : [],
    inventory_id: searchParams.get("inventory_id")
      ? searchParams
          .get("inventory_id")!
          .split(",")
          .map((id) => ({ id: id, value: id }))
      : [],
    status: getStatusFromSearchParams(searchParams),
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    is_onelink: searchParams.get("is_onelink") || null,
  };

  const initialParams: CouponFilters = {
    page_no: parseInt(
      searchParams.get("page_no") || searchParams.get("page") || "1",
      10,
    ) || 1,
    search_text: (searchParams.get("search_text") || "").trim() || undefined,
    coupon_id: initialFilters.coupon_id.map((item) => Number(item.id)),
    order_id: initialFilters.order_id.map((item) => Number(item.id)),
    customer_id: initialFilters.customer_id.map((item) => Number(item.id)),
    inventory_id: initialFilters.inventory_id.map((item) => Number(item.id)),
    status: Number(initialFilters.status),
    redeemed: isRedeemedStatus(initialFilters.status) ? true : undefined,
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    is_onelink:
      initialFilters.is_onelink === "1"
        ? true
        : initialFilters.is_onelink === "0"
          ? false
          : undefined,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const [filters, setFilters] =
    useState<CouponsSelectedFilters>(initialFilters);
  const [params, setParams] = useState(initialParams);

  const [couponIdOptions, setCouponIdOptions] = useState<FilterValue[]>(
    initialFilters.coupon_id,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [selectedCouponDetails, setSelectedCouponDetails] =
    useState<Coupon | null>(null);
  const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);
  const {
    actionConfirm,
    actionExpiryDate,
    actionError,
    isActionLoading,
    bulkJob,
    isActivityLogsOpen,
    activityLogs,
    isActivityLogsLoading,
    activityLogsSubtitle,
  } = couponStore;

  /**
   * Opens the newer admin confirm dialog for a coupon action.
   * Cancel and a failed date check write nothing.
   *
   * @param next - Title, message, button label, and the write to run after confirm.
   * @returns Nothing.
   */
  const openConfirm = (next: CouponActionConfirm) => {
    couponStore.openActionConfirm(next);
  };

  /**
   * Closes the coupon confirm dialog without writing.
   *
   * @returns Nothing.
   */
  const closeConfirm = () => {
    couponStore.closeActionConfirm();
  };

  /**
   * Closes the coupons dialog after a failed bulk job and leaves the list as it is.
   *
   * @returns Nothing.
   */
  const dismissBulkPopup = () => {
    couponStore.dismissBulkJob();
  };

  /**
   * Runs the pending coupon action after the newer confirm dialog is accepted.
   * The button shows a spinner until the request returns.
   * Delete all, Expire all, and Reactivate all keep this dialog open and show the job bar.
   * A missing or past expiry date stays on the dialog and writes nothing.
   *
   * @returns Nothing.
   */
  const submitConfirm = () => {
    if (!actionConfirm || isActionLoading) return;
    if (actionConfirm.form) {
      if (!actionExpiryDate) {
        couponStore.setActionError("Please choose an expiry date.");
        return;
      }
      const today = new Date();
      const todayText = `${today.getFullYear()}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      if (actionExpiryDate < todayText) {
        couponStore.setActionError("Expiry date must be today or a future date.");
        return;
      }
    }
    const run = actionConfirm.run;
    const needsDate = actionConfirm.form;
    couponStore.markActionSubmitting();
    Promise.resolve(run(needsDate ? actionExpiryDate : undefined)).finally(
      () => {
        couponStore.finishActionSubmit();
      },
    );
  };

  const handleActionsMenuOpen = () => {
    if (!actionsAnchorRef.current) return;
    setActionsMenuAnchor(actionsAnchorRef.current);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const getSelectedCoupons = () =>
    couponStore.coupons.filter((coupon) =>
      selectedShipmentIdsMap.has(coupon.id),
    );

  const isActionDisabled = (actionId: string) => {
    const selected = getSelectedCoupons();
    const hasSelection = selected.length > 0;
    const oneOrder = Boolean(resolveSingleOrderId());
    const onActiveList = params.active !== false;

    if (actionId === "send_reminders") return !hasSelection;

    if (
      actionId === "expire" ||
      actionId === "expire_all" ||
      actionId === "delete" ||
      actionId === "delete_all" ||
      actionId === "change_expiry" ||
      actionId === "reactivate" ||
      actionId === "reactivate_all"
    ) {
      const visible = couponWriteActions(
        selected,
        onActiveList,
        params.status,
        oneOrder,
      );
      if (!visible.includes(actionId)) return true;
      return isCouponWriteGray(actionId, selected, oneOrder);
    }

    return false;
  };

  const resolveSingleOrderId = () => {
    const fromFilter = getSingleId(params.order_id);
    if (fromFilter) return fromFilter;

    const orderIds = Array.from(
      new Set(
        getSelectedCoupons()
          .map((coupon) => Number(coupon.order_id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );

    return orderIds.length === 1 ? orderIds[0] : undefined;
  };

  const resolveSingleCustomerId = () => {
    const fromFilter = getSingleId(params.customer_id);
    if (fromFilter) return fromFilter;

    const customerIds = Array.from(
      new Set(
        getSelectedCoupons()
          .map((coupon) => Number(coupon.customer_id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );

    return customerIds.length === 1 ? customerIds[0] : undefined;
  };

  const handleExpireSelectedCoupons = () => {
    if (selectedShipmentIdsMap.size === 0)
      return showWarning("Please select coupons to expire.");

    if (Array.isArray(params.order_id) && params.order_id.length > 1)
      return showWarning(
        "To expire coupons, please make sure only one order is selected in the filters.",
      );

    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    const selectedCoupons = getSelectedCoupons();
    const inactiveCoupons = selectedCoupons.filter(
      (coupon) => !isCouponActive(coupon),
    );
    if (inactiveCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(inactiveCoupons)} cannot be expired because they are not active.`,
      );

    const onelinkCoupons = selectedCoupons.filter((coupon) =>
      Boolean(coupon.is_onelink),
    );
    if (onelinkCoupons.length > 0)
      return showWarning(
        "Expiring onelink coupons is not allowed. Please contact Swageazy support team.",
      );

    const redeemedCoupons = selectedCoupons.filter((coupon) =>
      Boolean(coupon.redeemed),
    );
    if (redeemedCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(redeemedCoupons)} cannot be expired because they are already redeemed.`,
      );

    const alreadyExpiredCoupons = selectedCoupons.filter((coupon) =>
      Boolean(coupon.expired),
    );
    if (alreadyExpiredCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(alreadyExpiredCoupons)} cannot be expired because they are already expired.`,
      );

    const couponIds = Array.from(selectedShipmentIdsMap.keys());
    openConfirm({
      title: "Expire coupons",
      message: `Are you sure you want to expire ${couponIds.length} coupon${
        couponIds.length > 1 ? "s" : ""
      }?`,
      confirmText: "Expire",
      form: false,
      run: () =>
        couponStore
          .expireCoupons({ ...params, order_id: orderId }, couponIds)
          .then((response) => {
            showSuccess(response.message);
            handleApply();
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const handleReactivateSelectedCoupons = () => {
    if (selectedShipmentIdsMap.size === 0)
      return showWarning("Please select coupons to reactivate.");

    if (Array.isArray(params.order_id) && params.order_id.length > 1)
      return showWarning(
        "To reactivate coupons, please make sure only one order is selected in the filters.",
      );

    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    const selectedCoupons = getSelectedCoupons();
    const onelinkCoupons = selectedCoupons.filter((coupon) =>
      Boolean(coupon.is_onelink),
    );
    if (onelinkCoupons.length > 0)
      return showWarning(
        "Reactivating onelink coupons is not allowed. Please contact Swageazy support team.",
      );

    const redeemedCoupons = selectedCoupons.filter((coupon) =>
      Boolean(coupon.redeemed),
    );
    if (redeemedCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(redeemedCoupons)} cannot be reactivated because they are already redeemed.`,
      );

    const stillActiveCoupons = selectedCoupons.filter((coupon) => {
      if (!isCouponActive(coupon)) return false;
      if (hasCouponExpiryPassed(coupon) || coupon.expired) return false;
      return true;
    });
    if (stillActiveCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(stillActiveCoupons)} cannot be reactivated because they are still active and the expiry date has not passed.`,
      );

    const couponIds = Array.from(selectedShipmentIdsMap.keys());
    openConfirm({
      title: "Reactivate coupons",
      message: `Are you sure you want to reactivate ${couponIds.length} coupon${
        couponIds.length > 1 ? "s" : ""
      }?`,
      confirmText: "Reactivate",
      form: false,
      run: () =>
        couponStore
          .reactivateCoupons({ ...params, order_id: orderId }, couponIds)
          .then((response) => {
            showSuccess(response.message);
            handleApply();
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const handleDeleteSelectedCoupons = () => {
    if (selectedShipmentIdsMap.size === 0)
      return showWarning("Please select coupons to delete.");

    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    const selectedCoupons = getSelectedCoupons();
    const blocked = selectedCoupons.find(
      (coupon) =>
        !isCouponActive(coupon) ||
        Boolean(coupon.is_onelink) ||
        Boolean(coupon.redeemed) ||
        Boolean(coupon.expired),
    );
    if (blocked)
      return showWarning(
        "Only active coupons that are not redeemed, not expired, and not One Link can be deleted.",
      );

    const couponIds = Array.from(selectedShipmentIdsMap.keys());
    openConfirm({
      title: "Delete coupons",
      message: `Are you sure you want to delete ${couponIds.length} coupon${
        couponIds.length > 1 ? "s" : ""
      }?`,
      confirmText: "Delete",
      form: false,
      run: () =>
        couponStore
          .deleteCoupons(orderId, couponIds)
          .then((response) => {
            showSuccess(response.message);
            handleApply();
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const beginBulkJob = (
    verb: string,
    response: { result?: { jobId?: number; queuedCount?: number; skippedCount?: number } },
  ) => {
    couponStore.startBulkJob(verb, response);
  };

  const handleDeleteAllOnOrder = () => {
    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    openConfirm({
      title: "Delete coupons",
      message: `Are you sure you want to delete every eligible coupon on order ${orderId}?`,
      confirmText: "Delete",
      form: false,
      run: () =>
        couponStore
          .deleteAllOnOrder(orderId)
          .then((response) => {
            beginBulkJob("Deleting", response);
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const handleExpireAllOnOrder = () => {
    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    openConfirm({
      title: "Expire coupons",
      message: `Are you sure you want to expire every eligible coupon on order ${orderId}?`,
      confirmText: "Expire",
      form: false,
      run: () =>
        couponStore
          .expireAllOnOrder(orderId)
          .then((response) => {
            beginBulkJob("Expiring", response);
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const handleReactivateAllOnOrder = () => {
    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    openConfirm({
      title: "Reactivate coupons",
      message: `Are you sure you want to reactivate every eligible coupon on order ${orderId}?`,
      confirmText: "Reactivate",
      form: false,
      run: () =>
        couponStore
          .reactivateAllOnOrder(orderId)
          .then((response) => {
            beginBulkJob("Reactivating", response);
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const handleChangeExpiryDate = () => {
    if (selectedShipmentIdsMap.size === 0)
      return showWarning("Please select coupons to update.");

    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    const selectedCoupons = getSelectedCoupons();
    const blocked = selectedCoupons.find(
      (coupon) =>
        !isCouponActive(coupon) ||
        Boolean(coupon.is_onelink) ||
        Boolean(coupon.redeemed),
    );
    if (blocked)
      return showWarning(
        "Only active coupons that are not redeemed and not One Link can have their expiry date changed.",
      );

    const couponIds = Array.from(selectedShipmentIdsMap.keys());
    openConfirm({
      title: "Change expiry date",
      message: `Are you sure you want to change the expiry date of ${couponIds.length} coupon${
        couponIds.length > 1 ? "s" : ""
      }?`,
      confirmText: "Update",
      form: true,
      run: (input) =>
        couponStore
          .updateExpiryDate(orderId, couponIds, input || "")
          .then((response) => {
            showSuccess(response.message);
            handleApply();
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          }),
    });
  };

  const handleActionSelect = (actionId: string) => {
    if (actionId === "send_reminders") {
      handleSendReminders();
    } else if (actionId === "download_shipments") {
      downloadShipmentsReport();
    } else if (actionId === "download_report") {
      downloadReport();
    } else if (actionId === "expire") {
      handleExpireSelectedCoupons();
    } else if (actionId === "expire_all") {
      handleExpireAllOnOrder();
    } else if (actionId === "delete") {
      handleDeleteSelectedCoupons();
    } else if (actionId === "delete_all") {
      handleDeleteAllOnOrder();
    } else if (actionId === "change_expiry") {
      handleChangeExpiryDate();
    } else if (actionId === "reactivate") {
      handleReactivateSelectedCoupons();
    } else if (actionId === "reactivate_all") {
      handleReactivateAllOnOrder();
    }
    handleActionsMenuClose();
  };

  const handleViewDetails = (coupon: Coupon) => {
    setSelectedCouponDetails(coupon);
    setIsDetailsPopupOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsPopupOpen(false);
    setSelectedCouponDetails(null);
  };

  let apiTimer: any;

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_COUPONS)) {
      setIsLoading(true);
      couponStore
        .getCoupons(params)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          if (key === "customer_id") {
            const paramValue = filters.customer_id
              .map((item) => item.id + ":" + item.value)
              .join(",");
            searchParams.set(key, paramValue);
          } else if (key === "is_onelink") {
            const onelinkValue = filters.is_onelink;
            if (onelinkValue !== null) {
              searchParams.set("is_onelink", onelinkValue);
            }
          } else if (key === "status") {
            const statusValue = Number(value);
            if (statusValue > 0) {
              searchParams.set("status", String(statusValue));
            }
            if (isRedeemedStatus(statusValue)) {
              searchParams.set("redeemed", "true");
            }
          } else if (key === "redeemed") {
            if (value === true) {
              searchParams.set("redeemed", "true");
            }
          } else {
            const paramValue = Array.isArray(value)
              ? value.join(",")
              : value.toString();
            searchParams.set(key, paramValue);
          }
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleApply = () => {
    const searchText = filters.search_text.trim();
    setSelectedShipmentIdsMap(new Map());
    setParams((prev) => ({
      ...prev,
      search_text: searchText || undefined,
      coupon_id: filters.coupon_id.map((item) => Number(item.id)),
      order_id: filters.order_id.map((item) => Number(item.id)),
      inventory_id: filters.inventory_id.map((item) => Number(item.id)),
      customer_id: filters.customer_id.map((item) => Number(item.id)),
      status: Number(filters.status),
      redeemed: isRedeemedStatus(filters.status) ? true : undefined,
      active: filters.active,
      is_onelink:
        filters.is_onelink === "1"
          ? true
          : filters.is_onelink === "0"
            ? false
            : undefined,
      page_no: 1,
    }));
  };

  useEffect(() => {
    if (!bulkJob || bulkJob.finished) return;
    const jobId = bulkJob.jobId;
    const verb = bulkJob.verb;
    const skippedCount = bulkJob.skippedCount;
    let stopped = false;
    let timer: number | undefined;
    const readJob = () => {
      couponStore
        .getCouponJob(jobId)
        .then((response) => {
          if (stopped) return;
          const job = response.result || {};
          const finished = Boolean(job.finished);
          if (finished) {
            stopped = true;
            if (timer) window.clearInterval(timer);
            const line = job.success
              ? shortCouponJobMessage(verb, job.summary, skippedCount)
              : "The coupon update failed. Please try again.";
            couponStore.patchBulkJob(jobId, {
              progress: job.success ? 100 : couponStore.bulkJob?.progress || 0,
              finished: true,
              success: Boolean(job.success),
              summary: line,
            });
            if (!job.success) return;
            window.setTimeout(() => {
              couponStore.completeBulkJob();
              handleApply();
            }, 2000);
            return;
          }
          couponStore.patchBulkJob(jobId, {
            progress: Number(job.progress) || 0,
            finished: false,
            success: Boolean(job.success),
            summary: String(job.summary || "").startsWith("Processed")
              ? job.summary
              : couponStore.bulkJob?.summary,
          });
        })
        .catch(() => {
          // A missed check waits for the next one. The bar stays up.
        });
    };
    readJob();
    timer = window.setInterval(readJob, 2000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
    // The read follows this job id until the server marks it finished.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJob?.jobId, bulkJob?.finished]);

  const resetFilters = () => {
    setSelectedShipmentIdsMap(new Map());
    setFilters({
      search_text: "",
      coupon_id: [],
      order_id: [],
      customer_id: [],
      inventory_id: [],
      status: "0",
      active: true,
      is_onelink: null,
    });
    setCouponIdOptions([]);
    setParams({
      page_no: 1,
      limit: 30,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      search_text: undefined,
      coupon_id: [],
      order_id: [],
      inventory_id: [],
      customer_id: [],
      status: 0,
      redeemed: undefined,
      active: true,
      is_onelink: undefined,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleCouponIdSearch = (searchText?: string) => {
    const id = searchText?.trim();
    if (!id || !/^\d+$/.test(id)) return;
    setCouponIdOptions((prev) => {
      if (prev.some((item) => item.id === id)) return prev;
      return [...prev, { id, value: id }];
    });
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {});
      }, 1500);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleInventoryIdsSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        inventoryStore.searchIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleSendReminders = () => {
    const selectedCoupons = getSelectedCoupons();
    if (selectedCoupons.length === 0) {
      return showWarning("Please select redemptions to send reminders.");
    }

    const inactiveCoupons = selectedCoupons.filter(
      (coupon) => !isCouponActive(coupon),
    );
    if (inactiveCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(inactiveCoupons)} cannot receive reminders because they are not active.`,
      );

    const redeemedCoupons = selectedCoupons.filter((coupon) =>
      Boolean(coupon.redeemed),
    );
    if (redeemedCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(redeemedCoupons)} cannot receive reminders because they are already redeemed.`,
      );

    const expiredCoupons = selectedCoupons.filter(
      (coupon) => Boolean(coupon.expired) || hasCouponExpiryPassed(coupon),
    );
    if (expiredCoupons.length > 0)
      return showWarning(
        `Coupon(s) ${getCouponIdsLabel(expiredCoupons)} cannot receive reminders because they are expired or the expiry date has passed.`,
      );

    couponStore
      .sendReminders(selectedCoupons.map((coupon) => coupon.id))
      .then((response) => {
        const ids = response.result as number[];
        if (ids && ids.length !== 0) {
          const message = `${response.message} Rejected ids are ${ids}`;
          showWarning(message);
        } else {
          showSuccess("Reminders sent.");
          setSelectedShipmentIdsMap(new Map());
        }
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const downloadShipmentsReport = () => {
    if (Array.isArray(params.order_id) && params.order_id.length > 1)
      return showWarning(
        "Please make sure only one order is selected in the filters.",
      );

    const orderId = resolveSingleOrderId();
    if (!orderId)
      return showWarning(
        "Please apply one Order ID filter, or select coupons from a single order.",
      );

    couponStore
      .downloadShipmentsReport({
        page_no: 1,
        limit: 200,
        order_id: orderId,
        redeemed: true,
        active: true,
        sort_by: "created_date",
        sort_type: SortType.DESC,
      })
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `RedemptionShipmentsReport.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showWarning(
            "Failed to download the Coupons shipments report csv file.",
          );
        }
      })
      .catch(async (err) => {
        let apiMessage = err?.response?.data?.message;
        if (!apiMessage && typeof err?.response?.data?.text === "function") {
          try {
            const parsed = JSON.parse(await err.response.data.text());
            apiMessage = parsed?.message;
          } catch {
            apiMessage = undefined;
          }
        }

        const message = apiMessage || getCaughtErrorMessage(err);
        if (isShipmentsDownloadEmptyError(message)) {
          showWarning(SHIPMENTS_DOWNLOAD_EMPTY_MESSAGE);
          return;
        }
        showError("Error", message);
      });
  };

  const downloadReport = () => {
    if (Array.isArray(params.customer_id) && params.customer_id.length > 1)
      return showWarning(
        "Please make sure only one customer is selected in the filters.",
      );

    const customerId = resolveSingleCustomerId();
    if (!customerId)
      return showWarning(
        "Please apply one Customer filter, or select coupons from a single customer.",
      );

    couponStore
      .downloadReport({ ...params, customer_id: customerId })
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `RedemptionReport.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showWarning("Failed to download the Coupons report csv file.");
        }
      })
      .catch((err) => {
        showError("Error", getCaughtErrorMessage(err));
      });
  };

  const renderFilters = () => (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <FormTextField
            id="coupon-recipient-email"
            title="Recipient Email"
            placeholder="Search by recipient email"
            type="search"
            variant="outlined"
            fontSize="12px"
            marginBottom={0}
            maxLength={MAX_RECIPIENT_SEARCH_LENGTH}
            borderColor="#ddd"
            value={filters.search_text}
            onChange={(_, value) => {
              setFilters((prev) => ({
                ...prev,
                search_text: value ?? "",
              }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Coupon Id"
            value={filters.coupon_id}
            fontSize="12px"
            list={[
              ...filters.coupon_id,
              ...couponIdOptions.filter(
                (option) =>
                  !filters.coupon_id.some((item) => item.id === option.id),
              ),
            ]}
            local={false}
            handleSearch={handleCouponIdSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                coupon_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Inventory Id"
            value={filters.inventory_id}
            fontSize="12px"
            list={inventoryStore.ids.map((item) => {
              return { id: String(item), value: String(item) };
            })}
            local={false}
            handleSearch={handleInventoryIdsSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                inventory_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Order Id"
            value={filters.order_id}
            fontSize="12px"
            list={orderStore.orderIds.map((item) => {
              return { id: String(item), value: String(item) };
            })}
            local={false}
            handleSearch={handleOrderIdSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                order_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Customers"
            value={filters.customer_id}
            fontSize="12px"
            list={customerStore.customers.map((item) => {
              return { id: String(item.id), value: item.name! };
            })}
            local={false}
            handleSearch={handleCustomerSearch}
            handleSelect={(items) => {
              setFilters((prev) => ({
                ...prev,
                customer_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppDropDown
            title="Status"
            isMultiSelect={false}
            value={String(filters.status)}
            list={[
              { id: "0", value: "Select" },
              { id: "1", value: "Not Sent" },
              { id: "2", value: "Sent" },
              { id: "3", value: "Opened" },
              { id: "4", value: "Pending Redemption" },
              { id: "5", value: "Redeemed" },
              { id: "6", value: "Expired" },
              { id: "7", value: "Reactivated" },
            ]}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              setFilters((prev) => ({
                ...prev,
                status: id,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppDropDown
            title="Active"
            isMultiSelect={false}
            value={filters.active ? "true" : "false"}
            list={[
              { id: "true", value: "Active" },
              { id: "false", value: "InActive" },
            ]}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              setFilters((prev) => ({
                ...prev,
                active: id === "true",
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppDropDown
            title="Type"
            isMultiSelect={false}
            value={filters.is_onelink || "-1"}
            list={[
              { id: "-1", value: "Select" },
              { id: "1", value: "OneLink" },
              { id: "0", value: "Redemption" },
            ]}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              setFilters((prev) => ({
                ...prev,
                is_onelink: id ? id : "-1",
              }));
            }}
          />
        </Grid2>
      </Grid2>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1.5,
          mt: 2,
        }}
      >
        <AppButton
          variant="transparentPrimary"
          label="Clear"
          onClick={resetFilters}
          customStyles={{ minWidth: 120 }}
        />
        <AppButton
          variant="primary"
          label="Apply"
          onClick={handleApply}
          customStyles={{ minWidth: 120 }}
        />
      </Box>
    </Box>
  );

  const isActionsMenuOpen = Boolean(actionsMenuAnchor);
  const hasAnyActionPermission =
    userStore.hasPermission(EmployeeRoles.SEND_REDEMPTION_REMINDERS) ||
    userStore.hasPermission(EmployeeRoles.DOWNLOAD_REDEMPTION_REPORT);
  const isActionsDisabled = !hasAnyActionPermission;

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission(EmployeeRoles.VIEW_COUPONS) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">Coupons</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Monitor coupon redemptions, reminders, and export detailed
                reports.
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission(EmployeeRoles.VIEW_COUPONS) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">Coupons</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Monitor coupon redemptions, reminders, and export detailed
                reports.
              </CustomTypography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 1,
              }}
            >
              <AppButton
                variant="grayscale"
                label="Activity logs"
                onClick={() => {
                  couponStore.loadActivityLogs().catch((err) => {
                    showError("Error", getCaughtErrorMessage(err));
                  });
                }}
                customStyles={{ minWidth: 150 }}
              />
              {hasAnyActionPermission && (
                <Box ref={actionsAnchorRef}>
                  <AppButton
                    variant="grayscale"
                    label={`Actions${
                      selectedShipmentIdsMap.size > 0
                        ? ` (${selectedShipmentIdsMap.size})`
                        : ""
                    }`}
                    onClick={handleActionsMenuOpen}
                    disabled={isActionsDisabled}
                    customStyles={{ minWidth: 180 }}
                  />
                </Box>
              )}
            </Box>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={isActionsMenuOpen}
              onClose={handleActionsMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableAutoFocusItem
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow:
                      "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                    minWidth: 240,
                    overflow: "hidden",
                  },
                },
              }}
            >
              {ACTION_OPTIONS.filter((option) => {
                if (option.id === "send_reminders") {
                  return userStore.hasPermission(
                    EmployeeRoles.SEND_REDEMPTION_REMINDERS,
                  );
                }
                if (
                  option.id === "download_shipments" ||
                  option.id === "download_report"
                ) {
                  return userStore.hasPermission(
                    EmployeeRoles.DOWNLOAD_REDEMPTION_REPORT,
                  );
                }
                if (
                  option.id === "expire" ||
                  option.id === "expire_all" ||
                  option.id === "delete" ||
                  option.id === "delete_all" ||
                  option.id === "change_expiry" ||
                  option.id === "reactivate" ||
                  option.id === "reactivate_all"
                ) {
                  return couponWriteActions(
                    getSelectedCoupons(),
                    params.active !== false,
                    params.status,
                    Boolean(resolveSingleOrderId()),
                  ).includes(option.id);
                }
                return false;
              }).map((option, index) => {
                const bulkBlocksAll =
                  Boolean(bulkJob && !bulkJob.finished) &&
                  (option.id === "expire_all" ||
                    option.id === "delete_all" ||
                    option.id === "reactivate_all");
                const isItemDisabled =
                  isActionDisabled(option.id) || bulkBlocksAll;

                return (
                  <MenuItem
                    key={option.id}
                    onClick={() => handleActionSelect(option.id)}
                    disabled={isItemDisabled}
                    sx={{
                      fontSize: "0.9rem",
                      px: 2,
                      py: 1.25,
                      gap: 1,
                      borderTop:
                        index === 0
                          ? "none"
                          : "1px solid " + theme.palette.divider,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.customColors?.lightPurple ||
                          theme.palette.action.hover,
                      },
                      "&.Mui-disabled": {
                        opacity: 0.4,
                      },
                    }}
                  >
                    <CustomTypography variant="body2" fontWeight={600}>
                      {option.value}
                    </CustomTypography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>{renderFilters()}</Box>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {couponStore.coupons.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No coupons match the current filters.
                  </CustomTypography>
                </Box>
              )}
              {couponStore.coupons.length > 0 && (
                <CouponsTable
                  maxHeight={window.innerHeight - 350}
                  coupons={couponStore.coupons}
                  allSelected={
                    selectedShipmentIdsMap.size === couponStore.coupons.length
                  }
                  limit={params.limit || couponStore.limit}
                  page_no={params.page_no}
                  total_count={couponStore.total_count}
                  total_pages={couponStore.total_pages}
                  sortBy={params.sort_by}
                  sortType={params.sort_type}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column, sortType) => {
                    setParams((prev) => ({
                      ...prev,
                      sort_by: column,
                      sort_type: sortType,
                    }));
                  }}
                  selectedShipmentIdsMap={selectedShipmentIdsMap}
                  selectAllItem={() => {
                    const map = new Map<number, number>();
                    couponStore.coupons.forEach((item) =>
                      map.set(item.id, item.id),
                    );
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectAllItem={() => {
                    setSelectedShipmentIdsMap(new Map());
                  }}
                  selectItem={(item) => {
                    const map = new Map(selectedShipmentIdsMap);
                    map.set(item.id, item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectItem={(item) => {
                    const map = new Map(selectedShipmentIdsMap);
                    map.delete(item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  onViewDetails={handleViewDetails}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
      <CouponDetailsPopup
        open={isDetailsPopupOpen}
        coupon={selectedCouponDetails}
        onClose={handleCloseDetails}
      />
      <ActivityLogDrawer
        isOpen={isActivityLogsOpen}
        onClose={couponStore.closeActivityLogs}
        activityLogs={activityLogs}
        isLoading={isActivityLogsLoading}
        title="Coupon activity logs"
        subtitle={activityLogsSubtitle}
      />
      <CouponActionPopup
        open={actionConfirm !== null}
        title={actionConfirm?.title || "Are you sure?"}
        message={actionConfirm?.message || ""}
        confirmText={actionConfirm?.confirmText || "Apply"}
        cancelText="Cancel"
        loading={isActionLoading}
        confirmDisabled={Boolean(actionConfirm?.form) && !actionExpiryDate}
        error={actionError}
        progress={
          bulkJob
            ? {
                summary: bulkJob.finished
                  ? bulkJob.summary
                  : bulkJob.progress > 0 &&
                      String(bulkJob.summary).startsWith("Processed")
                    ? bulkJob.summary
                    : `Queued ${bulkJob.queuedCount} coupons.`,
                percent: bulkJob.finished ? 100 : bulkJob.progress,
                finished: bulkJob.finished,
                success: bulkJob.success,
              }
            : null
        }
        onCancel={closeConfirm}
        onConfirm={submitConfirm}
        onDismiss={dismissBulkPopup}
      >
        {actionConfirm?.form ? (
          <TextField
            fullWidth
            type="date"
            label="Expiry date"
            value={actionExpiryDate}
            onChange={(event) => {
              couponStore.setActionExpiryDate(event.target.value);
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                min: `${new Date().getFullYear()}-${String(
                  new Date().getMonth() + 1,
                ).padStart(2, "0")}-${String(new Date().getDate()).padStart(
                  2,
                  "0",
                )}`,
              },
            }}
          />
        ) : null}
      </CouponActionPopup>
    </Grid2>
  );
};

export default observer(CouponsListing);
